import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";

/**
 * Your Connexis managers — the people partners are handed to, one per partner.
 *
 * The house contact above stays as the fallback for anybody who hasn't been assigned. Assignment
 * itself happens on a partner's own row (Lenders · Introducers), because that is where you are
 * looking when you decide who should own the relationship.
 */

const BLANK = { name: "", title: "", phone: "", email: "", active: true };

export function ManagersCard() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);        // null | { id?, ...fields }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/hub/managers"); setData(r.data); }
    catch { setData({ items: [] }); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name?.trim() || form.name.trim().length < 2) { toast.error("Give them a name"); return; }
    setSaving(true);
    const body = { name: form.name.trim(), title: form.title?.trim() || null,
                   phone: form.phone?.trim() || null, email: form.email?.trim() || null };
    try {
      if (form.id) await api.patch(`/hub/managers/${form.id}`, body);
      else await api.post("/hub/managers", { ...body, active: true });
      toast.success(form.id ? `${body.name} updated` : `${body.name} added — assign them on a partner's row`);
      setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    try {
      const r = await api.delete(`/hub/managers/${m.id}`);
      toast.success(r.data.moved_to_house_contact
        ? `${m.name} removed — ${r.data.moved_to_house_contact} partner${r.data.moved_to_house_contact === 1 ? "" : "s"} moved to the house contact`
        : `${m.name} removed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that"); }
  };

  if (!data) {
    return (
      <div className="card grid place-items-center p-10 text-slate-400" data-testid="managers-loading">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  const house = data.on_the_house_contact || {};
  const onHouse = (house.lenders || 0) + (house.referrers || 0);

  return (
    <div className="card p-5" data-testid="managers-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[13.5px] font-semibold text-slate-900">Your Connexis managers</div>
          <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-slate-500">
            Add the people who look after partners, then assign one on a desk or introducer&apos;s own
            row. Their name, mobile and WhatsApp become that partner&apos;s support card. Anyone
            unassigned keeps seeing the house contact above.
          </p>
        </div>
        {!form && (
          <button onClick={() => setForm({ ...BLANK })} data-testid="managers-add"
                  className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-[10px] bg-[#0055FF] px-4 text-[13px] font-bold text-white transition-colors hover:bg-[#0044CC]">
            <Plus className="size-4" /> Add a manager
          </button>
        )}
      </div>

      {form && (
        <div className="mt-4 rounded-[12px] border border-slate-200 bg-slate-50/70 p-4" data-testid="managers-form">
          <div className="grid gap-3.5 sm:grid-cols-2">
            {[["name", "Name", "Lee McLean"], ["title", "Their title", "Partner manager"],
              ["phone", "Mobile for WhatsApp", "+44 7xxx xxxxxx"], ["email", "Email", "lee@connex.is"]].map(([k, label, ph]) => (
              <label key={k} className="block">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
                <input value={form[k] || ""} placeholder={ph} data-testid={`managers-field-${k}`}
                       onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                       className="mt-1.5 h-11 w-full rounded-[10px] border border-slate-200 bg-white px-3.5 text-[13.5px] text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#0055FF]" />
              </label>
            ))}
          </div>
          <div className="mt-3.5 flex justify-end gap-2.5">
            <button onClick={() => setForm(null)} data-testid="managers-cancel"
                    className="inline-flex h-10 items-center rounded-[10px] border border-slate-200 px-4 text-[13px] font-semibold text-slate-600 hover:bg-white">
              Cancel
            </button>
            <button onClick={save} disabled={saving} data-testid="managers-save"
                    className="inline-flex h-10 items-center rounded-[10px] bg-[#0055FF] px-5 text-[13px] font-bold text-white transition-colors hover:bg-[#0044CC] disabled:opacity-60">
              {saving ? "Saving…" : form.id ? "Save changes" : "Add them"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 divide-y divide-slate-100" data-testid="managers-list">
        {data.items.length === 0 && !form && (
          <div className="py-3 text-[12.5px] text-slate-500" data-testid="managers-empty">
            Nobody added yet — every partner is on the house contact.
          </div>
        )}
        {data.items.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`managers-row-${m.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold text-slate-900">{m.name}</div>
              <div className="mt-0.5 text-[12px] text-slate-500">
                {[m.title, m.phone, m.email].filter(Boolean).join(" · ") || "No details on file yet"}
              </div>
            </div>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[10.5px] font-semibold text-slate-600"
                  data-testid={`managers-count-${m.id}`}>
              {m.partners?.total || 0} partner{(m.partners?.total || 0) === 1 ? "" : "s"}
            </span>
            <button onClick={() => setForm({ id: m.id, name: m.name, title: m.title || "", phone: m.phone || "", email: m.email || "" })}
                    data-testid={`managers-edit-${m.id}`} title="Edit"
                    className="grid size-9 place-items-center rounded-[9px] text-slate-400 hover:bg-slate-50 hover:text-slate-700">
              <Pencil className="size-4" />
            </button>
            <button onClick={() => remove(m)} data-testid={`managers-delete-${m.id}`} title="Remove"
                    className="grid size-9 place-items-center rounded-[9px] text-slate-400 hover:bg-red-50 hover:text-red-600">
              <Trash2 className="size-4" />
            </button>
          </div>
        ))}
      </div>

      {data.items.length > 0 && (
        <div className="mt-3 text-[11.5px] text-slate-400" data-testid="managers-house-count">
          {onHouse} partner{onHouse === 1 ? "" : "s"} still on the house contact
          {house.lenders ? ` · ${house.lenders} desk${house.lenders === 1 ? "" : "s"}` : ""}
          {house.referrers ? ` · ${house.referrers} introducer${house.referrers === 1 ? "" : "s"}` : ""}.
        </div>
      )}
    </div>
  );
}
